import { useEffect, useState } from 'react'
import { CaregiverShortForm } from 'Pages/CaregiverPortal/ShortForm/CaregiverShortForm'
import { generatePath, useNavigate, useParams } from 'react-router'
import { useAuth } from 'hooks/use-auth'
import { CaregiverPortalHomePage } from 'Pages/CaregiverPortal/Home/HomePage'
import { type HomeState, type CaregiverShortFormState, type ChildShortFormLayoutState, CardType } from 'Pages/CaregiverPortal/types'
import { CAREGIVER_ENROLLMENTS, CAREGIVER_PORTAL_SHORT_FORM_SUBMITTED } from 'Pages/Routes'
import { deleteSession, initialEnrollmentDetails, saveSession, useCaregiverWizardContext, useCaregiverWizardUpdateDispatch } from 'ContextProviders/CaregiverWizard'
import { ChildShortFormLayout } from 'Pages/CaregiverPortal/ShortForm/ChildShortFormLayout'
import 'Pages/CaregiverPortal/CaregiverPortal.css'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { BannerModal } from 'Components/Modal/BannerModal'
import { type PortalLocation, type PortalChildDetails, type lookup } from 'core/types'
import { Modal } from 'Components/Modal'
import { Box } from '@mui/material'
import { toast } from 'react-toastify'
import { parseAddress } from 'Pages/CaregiverPortal/PortalHelpers'

enum Steps {
  Home,
  CaregiverShortForm,
  ChildShortForm
}

interface PageState {
  home: HomeState
  caregiver: CaregiverShortFormState
  child: ChildShortFormLayoutState
}

interface LongFormRedirect {
  redirect: boolean
  includeFormData: boolean
}

export function CaregiverPortalIndex (): JSX.Element {
  const { isdId, isdServiceProviderId } = useParams()
  const { login, user } = useAuth()
  const nav = useNavigate()
  const dispatch = useCaregiverWizardUpdateDispatch()
  const caregiverForm = useCaregiverWizardContext()
  const [isLoginConfirmationModalOpen, setIsLoginConfirmationModalOpen] = useState(false)
  const [isRestoreSessionModalOpen, setIsRestoreSessionModalOpen] = useState(false)
  const [languageList, setLanguageList] = useState<lookup[]>([])
  const [longFormRedirect, setLongFormRedirect] = useState<LongFormRedirect>({ redirect: false, includeFormData: false })
  const [showLongFormRedirectWarning, setShowLongFormRedirectWarning] = useState<{ show: boolean, includeFormData: boolean }>({ show: false, includeFormData: false })
  const [currentStep, setCurrentStep] = useState<Steps>(Steps.Home)
  const [pageState, setPageState] = useState<PageState>({
    home: { selectedCard: null, unbornChild: null, selectedServices: undefined },
    caregiver: { firstName: '', lastName: '', email: '', phone: '', doesNotHaveEmail: false, doesNotHavePhone: false, preferredContactMethodId: null, secondaryPhone: null, primaryLanguageId: null, currentlyHomeless: false, address: '', relationshipId: null },
    child: { children: [{ key: Date.now(), firstName: '', lastName: '', dateOfBirth: null, relationshipToChildId: null }] }
  })
  useEffect(() => {
    const startLongForm = async (): Promise<void> => {
      const tempForm = initialEnrollmentDetails()
      tempForm.isCaregiverOfChild = pageState.home.selectedCard === CardType.MY_CHILD
      tempForm.currentStep = tempForm.isCaregiverOfChild ? 0 : 1
      tempForm.unbornChild = pageState.home.unbornChild ?? false
      tempForm.isNewSession = false

      if (longFormRedirect.includeFormData) {
        const { location } = getCaregiverNameAddress(pageState.caregiver)

        tempForm.currentSubStep = 3

        tempForm.children = pageState.child.children.map((child): PortalChildDetails => {
          return {
            firstName: child.firstName,
            lastName: child.lastName,
            dateOfBirth: pageState.home.unbornChild === true ? null : child.dateOfBirth,
            dueDate: pageState.home.unbornChild === true ? child.dateOfBirth : null,
            birthGender: child.birthGender ?? undefined,
            languageID: child.primaryLanguageId ?? null,
            unborn: pageState.home.unbornChild ?? false,
            location,
            isHomeless: pageState.caregiver.currentlyHomeless ?? false,
            isCurrentlyUpdating: false,
            requestedServices: []
          }
        })

        tempForm.caregivers = [{
          firstName: pageState.caregiver.firstName,
          lastName: pageState.caregiver.lastName,
          email: pageState.caregiver.email,
          doesNotHaveEmail: pageState.caregiver.doesNotHaveEmail,
          primaryPhone: pageState.caregiver.phone,
          secondaryPhone: pageState.caregiver.secondaryPhone ?? '',
          preferredContactMethod: pageState.caregiver.preferredContactMethodId,
          location,
          isLivingWithStudent: true,
          hasFinancialResponsibility: true,
          isCurrentlyUpdating: false,
          // grab first child's relationship by default
          relationshipId: pageState.child.children[0].relationshipToChildId,
          primaryLanguageId: pageState.caregiver.primaryLanguageId
        }]
      } else if (pageState.home.selectedCard === CardType.MY_CHILD) {
        tempForm.currentSubStep = 1
      }

      dispatch({ type: 'form', form: tempForm })
      await saveSession(tempForm)

      const enrollmentRoute = generatePath(CAREGIVER_ENROLLMENTS, { isdId: isdId ?? '', isdServiceProviderId: isdServiceProviderId ?? '' })
      nav(enrollmentRoute)
    }

    if (longFormRedirect.redirect) {
      void startLongForm()
    }
  }, [longFormRedirect.redirect])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response: languages } = await sendGet('/Language/GetLookup?sort=true', {})
      setLanguageList(languages ?? [])
    }

    if ((currentStep === Steps.CaregiverShortForm || currentStep === Steps.ChildShortForm) && languageList.length === 0) {
      void fetchData()
    }
  }, [currentStep])

  useEffect(() => {
    if (caregiverForm?.isNewSession === false) {
      setIsRestoreSessionModalOpen(true)
    }
  }, [caregiverForm])

  const getCaregiverNameAddress = (caregiver: CaregiverShortFormState): { location: PortalLocation } => {
    const location = parseAddress(caregiver.address)

    return { location }
  }

  const handleSubmitHome = (): void => {
    if (pageState.home.selectedCard === CardType.MY_CHILD &&
      pageState.home.selectedServices !== undefined &&
        (pageState.home.selectedServices.length === 0 ||
          pageState.home.selectedServices.filter(x => !x.allowShortForm).length === 0)) {
      setCurrentStep(Steps.CaregiverShortForm)
    } else {
      setLongFormRedirect({ redirect: true, includeFormData: false })
    }
  }

  const handleSubmitCaregiverShortForm = async (): Promise<void> => {
    const { response: exists }: { response: boolean } = await sendGet(`/Account/DoesUserExist?email=${pageState.caregiver.email}`, {})
    if (exists) {
      setIsLoginConfirmationModalOpen(true)
    } else {
      setCurrentStep(Steps.ChildShortForm)
    }
  }

  const handleSubmitChildShortForm = (): void => {
    if (pageState.child.continueToLongForm === true) {
      setShowLongFormRedirectWarning({ show: true, includeFormData: true })
    } else {
      // assign the child to the intake agency for the selected ISD
      const submit = async (): Promise<void> => {
        const { location } = getCaregiverNameAddress(pageState.caregiver)

        const { response, error } = await sendPost(`/CaregiverPortal/SubmitShortForm?isdID=${isdId ?? 0}`, {
          caregiver: {
            firstName: pageState.caregiver.firstName,
            lastName: pageState.caregiver.lastName,
            email: pageState.caregiver.email,
            doesNotHaveEmail: pageState.caregiver.doesNotHaveEmail,
            phone: pageState.caregiver.phone,
            secondaryPhone: pageState.caregiver.secondaryPhone ?? '',
            preferredContactMethodID: pageState.caregiver.preferredContactMethodId,
            primaryLanguageID: pageState.caregiver.primaryLanguageId,
            isLivingWithStudent: true,
            isCurrentlyUpdating: false,
            currentlyHomeless: pageState.caregiver.currentlyHomeless ?? false
          },
          children: pageState.child.children,
          services: pageState.home.selectedServices?.map((service) => service.id) ?? [],
          location,
          unbornChild: pageState.home.unbornChild
        })

        if (error[0] !== '') {
          toast.error(error[0])
        } else {
          const submittedRoute = generatePath(CAREGIVER_PORTAL_SHORT_FORM_SUBMITTED, { isdId: isdId ?? '', isdServiceProviderId: isdServiceProviderId ?? '' })
          nav(submittedRoute, {
            state: {
              firstName: pageState.caregiver.firstName,
              lastName: pageState.caregiver.lastName,
              careManagements: response
            }
          })
        }
      }

      void submit()
    }
  }

  const handleRestoreSession = (): void => {
    setIsRestoreSessionModalOpen(false)
    const enrollmentRoute = generatePath(CAREGIVER_ENROLLMENTS, { isdId: isdId ?? '', isdServiceProviderId: isdServiceProviderId ?? '' })
    nav(enrollmentRoute)
  }

  const handleNewSession = (): void => {
    setIsRestoreSessionModalOpen(false)
    deleteSession()
    dispatch({ type: 'form', form: null })
  }

  return <>
    { currentStep === Steps.Home && <CaregiverPortalHomePage
      state={pageState.home}
      isdId={isdId}
      isdServiceProviderId={isdServiceProviderId}
      user={user}
      login={login}
      newSession={handleNewSession}
      setState={(newState) => { setPageState((prevState) => ({ ...prevState, home: newState })) }}
      onContinue={handleSubmitHome}
    />}
    { currentStep === Steps.CaregiverShortForm && <CaregiverShortForm
      pageState={pageState.caregiver}
      languageList={languageList}
      isdId={isdId}
      setPageState={(newState) => { setPageState((prevState) => ({ ...prevState, caregiver: newState })) }}
      onContinue={handleSubmitCaregiverShortForm}
      onBack={() => { setCurrentStep(Steps.Home) }}
    /> }
    { currentStep === Steps.ChildShortForm && <ChildShortFormLayout
      pageState={pageState.child}
      isdId={isdId}
      isIDK={pageState.home.selectedServices?.length === 0}
      isUnborn={pageState.home.unbornChild ?? false}
      languageList={languageList}
      setPageState={(newState) => { setPageState((prevState) => ({ ...prevState, child: newState })) }}
      onContinue={handleSubmitChildShortForm}
      onBack={() => { setCurrentStep(Steps.CaregiverShortForm) }}
    /> }

    {isLoginConfirmationModalOpen && <BannerModal
      open={isLoginConfirmationModalOpen}
      title={'Account Found'}
      content={<Box>There's already an account with the email <b>{pageState.caregiver.email}</b>.</Box>}
      confirmButtonText='Continue to Login'
      cancelButtonText='Close'
      data-testid='confirm-redirect-to-login'
      onConfirm={() => { login(pageState.caregiver.email) }}
      onClose={() => { setIsLoginConfirmationModalOpen(false) }}
    />}

    {showLongFormRedirectWarning.show && <BannerModal
      open={showLongFormRedirectWarning.show}
      title={'Redirecting to Another MiECC Page'}
      content={<Box>
        You're being redirected away from this page.
        { showLongFormRedirectWarning.includeFormData
          ? ' Your information will be saved.'
          : ' Your information will not be saved.'
        }
      </Box>}
      confirmButtonText='Continue'
      cancelButtonText='Cancel'
      data-testid='confirm-redirect-to-long-form'
      onConfirm={() => { setLongFormRedirect({ redirect: true, includeFormData: showLongFormRedirectWarning.includeFormData }) }}
      onClose={() => { setShowLongFormRedirectWarning({ show: false, includeFormData: false }) }}
    />}

    {isRestoreSessionModalOpen && <Modal
      onClose={() => { setIsRestoreSessionModalOpen(false) }}
      open={isRestoreSessionModalOpen}
      title={'Restore Session'}
      confirmationContent={<Box>Would you like to restore your previous session?</Box>}
      confirmButtonText={'Yes, Restore'}
      cancelButtonText='No, I will start over'
      maxWidth={'xs'}
      data-testid='session-restore-modal'
      onConfirm={handleRestoreSession}
      onCancel={handleNewSession}
    />}
  </>
}
